/**
 * Carregamento e revalidação — o mesmo ciclo dos rios e da fumaça.
 *
 * - `pontos.json` (~5 KB com gzip) desce quando a camada liga, e é revalidado
 *   a cada ciclo da atualização automática: o espelho publica de 5 em 5 min.
 * - `cams.json` (~50 KB) só desce com o modelo ligado; o espelho o reescreve
 *   de hora em hora.
 * - `cams-series.json` (~250 KB) só na primeira ficha de célula aberta.
 *
 * A revalidação é silenciosa ("Baixando…" piscando a cada ciclo é ruído) e,
 * quando falha com dado na tela, mantém o dado e mostra o erro.
 */
import { useNow } from '../ui/hooks';
import { BASE_AR, carregarCams, carregarCamsSeries, carregarPontos } from './api';
import { useArStore } from './store';
import { avisoDeFrescor } from './tempo';

/** Revalidações a menos disto do último pedido são ignoradas. */
const INTERVALO_MIN_MS = 55_000;

let voo: AbortController | null = null;
let vooModelo: AbortController | null = null;
let vooSeries: AbortController | null = null;
let ultimoPedido = 0;
let ultimoPedidoModelo = 0;

function erroDe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export async function carregarAr(force = false): Promise<void> {
  if (!BASE_AR) return;
  const { situacao, pontos } = useArStore.getState();
  if (!force && (situacao.estado === 'loading' || pontos.gerado !== null)) return;
  voo?.abort();
  const ctrl = new AbortController();
  voo = ctrl;
  ultimoPedido = Date.now();
  if (pontos.gerado === null) useArStore.setState({ situacao: { estado: 'loading', erro: null } });
  try {
    const novo = await carregarPontos(ctrl.signal);
    if (ctrl.signal.aborted) return;
    // Mesma publicação: não trocar poupa reconstruir a camada.
    if (novo.gerado !== null && novo.gerado === useArStore.getState().pontos.gerado) {
      useArStore.setState({ situacao: { estado: 'ready', erro: null } });
      return;
    }
    useArStore.setState({ pontos: novo, situacao: { estado: 'ready', erro: null } });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    useArStore.setState({ situacao: { estado: 'error', erro: erroDe(e) } });
  } finally {
    if (voo === ctrl) voo = null;
  }
}

export async function carregarModelo(force = false): Promise<void> {
  if (!BASE_AR) return;
  const { situacaoModelo, cams } = useArStore.getState();
  if (!force && (situacaoModelo.estado === 'loading' || cams)) return;
  vooModelo?.abort();
  const ctrl = new AbortController();
  vooModelo = ctrl;
  ultimoPedidoModelo = Date.now();
  if (!cams) useArStore.setState({ situacaoModelo: { estado: 'loading', erro: null } });
  try {
    const novo = await carregarCams(ctrl.signal);
    if (ctrl.signal.aborted) return;
    const atual = useArStore.getState().cams;
    if (atual && novo.gerado === atual.gerado && novo.h0 === atual.h0) {
      useArStore.setState({ situacaoModelo: { estado: 'ready', erro: null } });
      return;
    }
    // Grade nova: as séries da ficha (se já baixadas) são de outra janela.
    const series = useArStore.getState().series;
    useArStore.setState({
      cams: novo,
      situacaoModelo: { estado: 'ready', erro: null },
      ...(series && series.h0 !== novo.h0 ? { series: null, situacaoSeries: { estado: 'idle', erro: null } } : {}),
    });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    useArStore.setState({ situacaoModelo: { estado: 'error', erro: erroDe(e) } });
  } finally {
    if (vooModelo === ctrl) vooModelo = null;
  }
}

/** Séries completas do modelo, para a ficha de uma célula (uma vez por janela). */
export async function carregarSeries(): Promise<void> {
  if (!BASE_AR) return;
  const { situacaoSeries, series, cams } = useArStore.getState();
  if (situacaoSeries.estado === 'loading') return;
  if (series && (!cams || series.h0 === cams.h0)) return;
  vooSeries?.abort();
  const ctrl = new AbortController();
  vooSeries = ctrl;
  useArStore.setState({ situacaoSeries: { estado: 'loading', erro: null } });
  try {
    const s = await carregarCamsSeries(ctrl.signal);
    if (ctrl.signal.aborted) return;
    useArStore.setState({ series: s, situacaoSeries: { estado: 'ready', erro: null } });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    useArStore.setState({ situacaoSeries: { estado: 'error', erro: erroDe(e) } });
  } finally {
    if (vooSeries === ctrl) vooSeries = null;
  }
}

/** Revalidação do ciclo automático — só com a camada em uso. */
export function refreshAr(): void {
  const { visivel, estilo, pontos, situacao, cams, situacaoModelo } = useArStore.getState();
  if (!visivel) return;
  const agora = Date.now();
  if ((pontos.gerado !== null || situacao.estado === 'error') && agora - ultimoPedido >= INTERVALO_MIN_MS) {
    void carregarAr(true);
  }
  if (estilo.modelo && (cams || situacaoModelo.estado === 'error') && agora - ultimoPedidoModelo >= INTERVALO_MIN_MS) {
    void carregarModelo(true);
  }
}

export function iniciarSyncAr(): () => void {
  const garantir = () => {
    const { visivel, estilo } = useArStore.getState();
    if (!visivel) return;
    void carregarAr();
    if (estilo.modelo) void carregarModelo();
  };
  garantir();
  const aoVoltarConexao = () => {
    ultimoPedido = 0;
    ultimoPedidoModelo = 0;
    refreshAr();
  };
  if (typeof window !== 'undefined') window.addEventListener('online', aoVoltarConexao);
  const parar = useArStore.subscribe((a, b) => {
    if (a.visivel && !b.visivel) {
      // Religar depois de um tempo: o dado guardado pode ser de horas atrás.
      garantir();
      refreshAr();
    } else if (a.visivel && a.estilo.modelo && !b.estilo.modelo) {
      void carregarModelo();
    }
  });
  return () => {
    parar();
    if (typeof window !== 'undefined') window.removeEventListener('online', aoVoltarConexao);
  };
}

/** Ponto amarelo no trilho: camada ligada e dado com problema. */
export function useArDesatualizado(): boolean {
  const visivel = useArStore((s) => s.visivel);
  const erro = useArStore((s) => s.situacao.estado === 'error' && s.pontos.gerado === null);
  const gerado = useArStore((s) => s.pontos.gerado);
  // Relógio próprio: com a máquina parada o arquivo não muda.
  const agora = useNow(60_000);
  if (!visivel) return false;
  return erro || avisoDeFrescor(gerado, agora) !== null;
}

/** Só para os testes. */
export function resetArSync(): void {
  voo?.abort();
  vooModelo?.abort();
  vooSeries?.abort();
  voo = vooModelo = vooSeries = null;
  ultimoPedido = ultimoPedidoModelo = 0;
}
